import React, { useEffect, useMemo, useState } from 'react';
import UserFilterRail from './UserFilterRail';
import UserList from './UserList';
import UserDetailDrawer from './UserDetailDrawer';
import UsersViewTabs from './UsersViewTabs';
import LeaverRowAction from './LeaverRowAction';
import useLeaversSummary from './useLeaversSummary';
import { hasLeaverData, isLeaver, sortLeavers } from './leavers';
import { countByDept, userDeptIds } from './departmentFilter';
import { formatDateTime } from '../../../lib/formatDate';
import './usersTab.css';

/**
 * Orchestrator for the redesigned Users tab.
 *
 * Owns: search, role/department filter, sort.
 * Receives the user data + the currently selected user from AdminDashboard;
 * forwards selection upward via onSelect. Drawer body is a render-prop slot
 * (children) so AdminDashboard can keep the existing detail logic
 * (departments, tools, action items, policies) intact during the rollout.
 *
 * URL state: ?user=<id> is synced when a drawer is open so refresh/share
 * preserves the active row; ?view=leavers keeps the Leavers tab linkable.
 *
 * Props:
 *   users               array of user records from /api/users
 *   departments         array of {id, name}
 *   selectedUser        user | null  (parent-owned for now)
 *   onSelect            (user) => void
 *   onAddUserClick      () => void
 *   children            JSX rendered inside the drawer body
 */
export default function AdminUsersTab({
  users,
  departments,
  selectedUser,
  onSelect,
  onAddUserClick,
  children,
}) {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [deptFilterIds, setDeptFilterIds] = useState([]);
  const [sort, setSort] = useState('name');
  // 'all' | 'leavers'. Read once from the URL; only honoured when the viewer
  // can see leaver data (otherwise the tab bar doesn't exist).
  const [viewParam, setView] = useState(
    () => (new URLSearchParams(window.location.search).get('view') === 'leavers' ? 'leavers' : 'all'),
  );

  // Hydrate selectedUser from URL on first mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const u = params.get('user');
    if (u && !selectedUser) {
      const found = users.find((x) => String(x.id) === u);
      if (found) onSelect(found);
    }
    // eslint-disable-next-line
  }, [users.length]);

  // Reflect selectedUser → URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (selectedUser) {
      params.set('user', selectedUser.id);
    } else {
      params.delete('user');
    }
    const qs = params.toString();
    const url = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
    window.history.replaceState(null, '', url);
  }, [selectedUser]);

  // Leaver keys reach only connected_apps.view holders with the flag on.
  const showLeavers = useMemo(() => hasLeaverData(users), [users]);
  const leaversSummary = useLeaversSummary(showLeavers);
  // Unknown (never synced, or the summary failed) is not "none left": the count
  // reads ? and the filter shows a notice instead of rows.
  const leaversUnknown = leaversSummary.status === 'error'
    || (leaversSummary.status === 'ready' && leaversSummary.summary.checked === false);
  const view = showLeavers ? viewParam : 'all';
  const leaversView = view === 'leavers';

  // Reflect the active tab → URL (alongside ?user=).
  useEffect(() => {
    if (!showLeavers) return;
    const params = new URLSearchParams(window.location.search);
    if (leaversView) params.set('view', 'leavers');
    else params.delete('view');
    const qs = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
  }, [showLeavers, leaversView]);

  const leaversStaleAt = leaversSummary.status === 'ready' && leaversSummary.summary.checked && leaversSummary.summary.stale
    ? leaversSummary.summary.last_synced_at : null;

  const roleCounts = useMemo(() => {
    const out = { all: users.length, admin: 0, manager: 0, employee: 0, inactive: 0, leavers: 0 };
    for (const u of users) {
      if (isLeaver(u)) out.leavers++;
      if (u.is_active === false) out.inactive++;
      else if (u.is_admin) out.admin++;
      else if ((u.roles || []).includes('dept_manager')) out.manager++;
      else out.employee++;
    }
    return out;
  }, [users]);

  const matchesSearch = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (u) => !q || !!u.name?.toLowerCase().includes(q) || !!u.email?.toLowerCase().includes(q);
  }, [search]);

  // Department counts, per tab, so each number matches what selecting it shows.
  // All users: active users per department (membership or management), not
  // narrowed by search, so the badges match what an admin would expect to action.
  // Leavers: the leavers in each department after search, i.e. exactly the rows
  // that department lists on this tab.
  const deptCounts = useMemo(() => (leaversView
    ? countByDept(users.filter((u) => isLeaver(u) && matchesSearch(u)))
    : countByDept(users.filter((u) => u.is_active !== false))
  ), [users, leaversView, matchesSearch]);

  // On Leavers a department with no leavers is noise and is hidden, unless it is
  // selected (e.g. carried over from All users): it then stays visible with its
  // 0 so the empty list is explained and the selection can be cleared.
  const railDepartments = useMemo(() => (leaversView
    ? (departments || []).filter((d) => (deptCounts[d.id] || 0) > 0 || deptFilterIds.includes(d.id))
    : (departments || [])
  ), [departments, leaversView, deptCounts, deptFilterIds]);

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (!matchesSearch(u)) return false;
      // The Leavers tab replaces the Role filter (its rail section is hidden).
      if (leaversView) {
        if (!isLeaver(u)) return false;
      } else if (roleFilter !== 'all') {
        if (roleFilter === 'inactive' && u.is_active !== false) return false;
        if (roleFilter === 'admin' && !(u.is_admin && u.is_active !== false)) return false;
        if (roleFilter === 'manager' && !((u.roles || []).includes('dept_manager') && !u.is_admin && u.is_active !== false)) return false;
        if (roleFilter === 'employee' && !(!u.is_admin && !(u.roles || []).includes('dept_manager') && u.is_active !== false)) return false;
      }
      if (deptFilterIds.length > 0) {
        // Same ids the counts use (departmentFilter.js), so a count and its rows agree.
        const ids = userDeptIds(u);
        if (!deptFilterIds.some((id) => ids.has(id))) return false;
      }
      return true;
    });
  }, [users, matchesSearch, roleFilter, deptFilterIds, leaversView]);

  const sorted = useMemo(() => {
    // The Leavers list has its own order (longest-waiting first), whatever the sort control says.
    if (leaversView) return sortLeavers(filtered);
    const copy = [...filtered];
    if (sort === 'last_seen') {
      copy.sort((a, b) => {
        const av = a.last_signed_in ? new Date(a.last_signed_in).getTime() : 0;
        const bv = b.last_signed_in ? new Date(b.last_signed_in).getTime() : 0;
        return bv - av;
      });
    } else if (sort === 'recent') {
      copy.sort((a, b) => {
        const av = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bv = b.created_at ? new Date(b.created_at).getTime() : 0;
        return bv - av;
      });
    } else {
      copy.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    return copy;
  }, [filtered, sort, leaversView]);

  const onDeptToggle = (id) => {
    setDeptFilterIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const leaversUnchecked = leaversView && leaversUnknown;
  const otherFiltersActive = !!search.trim() || deptFilterIds.length > 0;
  let listNotice = null;
  if (leaversUnchecked) listNotice = "Couldn't check Odoo yet. The leavers list needs one successful Odoo sync.";
  else if (leaversView && leaversStaleAt) listNotice = `Odoo last synced on ${formatDateTime(leaversStaleAt)}. This list may be out of date.`;
  const emptyText = leaversView && !otherFiltersActive ? 'No leavers. Everyone with a Nexus account is still in Odoo.' : undefined;

  const onClearAll = () => {
    setRoleFilter('all');
    setDeptFilterIds([]);
    setSearch('');
  };

  return (
    <div className="ut-shell">
      <UserFilterRail
        roleFilter={roleFilter}
        onRoleChange={setRoleFilter}
        deptFilterIds={deptFilterIds}
        onDeptToggle={onDeptToggle}
        departments={railDepartments}
        roleCounts={roleCounts}
        deptCounts={deptCounts}
        onClearAll={onClearAll}
        showRole={!leaversView}
      />

      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, height: '100%', overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10, flexShrink: 0 }}>
          <button type="button" className="btn btn-primary" onClick={onAddUserClick}>
            Add user
          </button>
        </div>
        {showLeavers && (
          <UsersViewTabs
            view={view}
            onChange={setView}
            leaversCount={leaversUnknown ? '?' : roleCounts.leavers}
            panelId="ut-view-panel"
          />
        )}
        <div
          className="ut-viewpanel"
          {...(showLeavers ? { role: 'tabpanel', id: 'ut-view-panel', 'aria-labelledby': `ut-view-tab-${view}` } : {})}
        >
          {leaversView && (
            <p className="ut-leavers-intro">
              People who left Odoo or are serving notice. Close their apps, then deactivate their account.
            </p>
          )}
          <UserList
            users={leaversUnchecked ? [] : sorted}
            notice={listNotice}
            hideEmpty={leaversUnchecked}
            emptyText={emptyText}
            selectedId={selectedUser?.id || null}
            onSelect={onSelect}
            sort={sort}
            onSortChange={setSort}
            search={search}
            onSearchChange={setSearch}
            renderRowAction={leaversView ? (u) => <LeaverRowAction user={u} /> : null}
          />
        </div>
      </div>

      <UserDetailDrawer
        user={selectedUser}
        onClose={() => onSelect(null)}
      >
        {children}
      </UserDetailDrawer>
    </div>
  );
}
